// Resolve which drop target sits under a point during an external mail drag.
//
// New Outlook / Outlook on the web deliver the drag as an Office
// DragAndDropEvent whose `taskPaneX` / `taskPaneY` are the cursor position
// relative to the add-in's task pane — i.e. relative to our document's own
// viewport — so `document.elementFromPoint` maps them straight onto our DOM.
//
// Two markers exist:
//  - `data-drop-folder-id` on a folder block → drop into THAT folder. A folder
//    block is always nested inside its project section, so when the cursor is
//    over a folder the folder marker is the NEAREST ancestor and wins.
//  - `data-drop-container-id` on a whole project section → drop at the project
//    ROOT (folder_id null). This catches the project header, root-level mail
//    rows, and blank space inside the project that isn't a folder.
//
// Over blank space outside any project, both are absent → null (the caller
// treats that as "no valid target": no highlight, no filing).
export const DROP_FOLDER_ATTR = 'data-drop-folder-id';
export const DROP_CONTAINER_ATTR = 'data-drop-container-id';

export type DropTarget =
  | { kind: 'folder'; folderId: string }
  | { kind: 'root'; containerId: string };

export function dropTargetAtPoint(x: number, y: number): DropTarget | null {
  if (typeof document === 'undefined' || typeof document.elementFromPoint !== 'function') {
    return null;
  }
  const el = document.elementFromPoint(x, y) as Element | null;
  if (!el?.closest) return null;
  // Folder wins when present: a folder block is always a descendant of its
  // project section, so a folder match is never farther than a container match.
  const folderEl = el.closest(`[${DROP_FOLDER_ATTR}]`) as HTMLElement | null;
  const folderId = folderEl?.getAttribute(DROP_FOLDER_ATTR);
  if (folderId) return { kind: 'folder', folderId };
  const containerEl = el.closest(`[${DROP_CONTAINER_ATTR}]`) as HTMLElement | null;
  const containerId = containerEl?.getAttribute(DROP_CONTAINER_ATTR);
  if (containerId) return { kind: 'root', containerId };
  return null;
}

/**
 * @deprecated Kept for callers that only care about a folder id. Prefer
 * {@link dropTargetAtPoint}.
 */
export function folderIdAtPoint(x: number, y: number): string | null {
  const t = dropTargetAtPoint(x, y);
  return t?.kind === 'folder' ? t.folderId : null;
}
